import { type Cadre, cadreEntier, cadreValide, ordonnerCoins, type Point } from '@admin-pmvb/core'

// Bords d'une page photographiée, cherchés sur le téléphone dans une copie réduite de la photo
// (fonction pure sur les pixels, sans bibliothèque d'image). Une feuille est plus claire et
// moins colorée que ce qui l'entoure : seuil d'Otsu sur la clarté moins la saturation, après
// un flou et une fermeture qui effacent le texte. La plus grande région claire, plus les
// morceaux que lui sépare une bande sombre (en-tête, tableau) s'ils forment avec elle une
// feuille, donne une enveloppe convexe ; on y prend le quadrilatère d'aire maximale, dont
// chaque côté est repoussé jusqu'à contenir toute la région, pour ne jamais couper le
// document. Au moindre doute (contraste faible, forme qui n'est pas une feuille, fond plus
// clair que le document, feuille qui remplit la photo) : null, la photo reste entière et la
// personne peut placer les coins elle-même.

export type ImageRgba = { largeur: number; hauteur: number; donnees: ArrayLike<number> }

/** Grand côté de la copie analysée : assez fin pour les coins, assez petit pour être instantané. */
export const COTE_ANALYSE = 512

/** Part de la variance expliquée par le seuil (Otsu) : en deçà, pas deux régions nettes. */
const SEPARATION_MIN = 0.75
/** Écart minimal de clarté entre la feuille et le fond (0 à 255). */
const ECART_MIN = 40
/** Aire minimale de la feuille, en fraction de la photo. */
const AIRE_MIN = 0.1
/** Aire minimale d'un morceau de feuille séparé du reste par une bande sombre. */
const AIRE_MORCEAU = 0.01
const MORCEAUX_MAX = 8
/** Part du cadre couverte par la feuille (trous du texte bouchés) : sinon, pas une feuille. */
const REMPLISSAGE_MIN = 0.85
/** Angles d'une feuille vue en perspective, en degrés. */
const ANGLE_MIN = 40
const ANGLE_MAX = 140
/** Sommets d'enveloppe gardés pour chercher le quadrilatère d'aire maximale. */
const SOMMETS_MAX = 40

type Forme = { quad: Point[]; remplissage: number }

export function detecterPage(img: ImageRgba): Cadre | null {
  const { largeur: L, hauteur: H, donnees } = img
  if (L < 16 || H < 16) return null
  const n = L * H
  const papier = new Float32Array(n)
  for (let i = 0, o = 0; i < n; i++, o += 4) {
    const r = donnees[o]!
    const g = donnees[o + 1]!
    const b = donnees[o + 2]!
    const clarte = 0.299 * r + 0.587 * g + 0.114 * b
    papier[i] = Math.max(0, clarte - (Math.max(r, g, b) - Math.min(r, g, b)))
  }
  const grand = Math.max(L, H)
  const flou = flouter(papier, L, H, Math.max(1, Math.round(grand / 170)))
  const s = otsu(flou)
  if (!s || s.separation < SEPARATION_MIN || s.ecart < ECART_MIN) return null

  const clair = new Uint8Array(n)
  for (let i = 0; i < n; i++) clair[i] = flou[i]! > s.seuil ? 1 : 0
  const { etiquettes, aires } = regions(
    fermer(clair, L, H, Math.max(1, Math.round(grand / 100))),
    L,
    H,
  )
  const parTaille = aires
    .map((aire, etiquette) => ({ aire, etiquette }))
    .filter((r) => r.etiquette > 0)
    .sort((a, b) => b.aire - a.aire)
  const base = parTaille[0]
  if (!base || base.aire < AIRE_MIN * n) return null

  const masque = new Uint8Array(n)
  for (let i = 0; i < n; i++) if (etiquettes[i] === base.etiquette) masque[i] = 1
  let forme = formeDe(masque, L, H)
  // Morceaux séparés par une bande sombre : gardés s'ils font une feuille avec le reste.
  for (const morceau of parTaille.slice(1, 1 + MORCEAUX_MAX)) {
    if (morceau.aire < AIRE_MORCEAU * n) break
    const essai = masque.slice()
    for (let i = 0; i < n; i++) if (etiquettes[i] === morceau.etiquette) essai[i] = 1
    const f = formeDe(essai, L, H)
    if (f && f.remplissage >= REMPLISSAGE_MIN) {
      masque.set(essai)
      forme = f
    }
  }
  if (!forme || forme.remplissage < REMPLISSAGE_MIN) return null
  const quad = forme.quad
  for (let k = 0; k < 4; k++) {
    const a = angle(quad[(k + 3) % 4]!, quad[k]!, quad[(k + 1) % 4]!)
    if (a < ANGLE_MIN || a > ANGLE_MAX) return null
  }
  const borne = (v: number) => Math.min(1, Math.max(0, v))
  const cadre = ordonnerCoins(quad.map(([x, y]) => [borne(x / L), borne(y / H)] as const))
  return cadreValide(cadre) && !cadreEntier(cadre) ? cadre : null
}

/** Quadrilatère qui contient la région, et part de ce quadrilatère qu'elle couvre (trous bouchés). */
function formeDe(masque: Uint8Array, L: number, H: number): Forme | null {
  const enveloppe = enveloppeConvexe(extremites(masque, L, H))
  if (enveloppe.length < 4) return null
  const quad = repousserCotes(quadrilatereMax(enveloppe), enveloppe)
  if (!quad) return null
  const a = aire(quad)
  return a > 0 ? { quad, remplissage: aireRemplie(masque, L, H) / a } : null
}

/** Sommes sur une boîte de rayon `r` (ligne puis colonne), bornée à l'image. */
function sommesBoite(
  v: ArrayLike<number>,
  L: number,
  H: number,
  r: number,
): { sommes: Float32Array; taille: (i: number) => number } {
  const tmp = new Float32Array(L * H)
  const sommes = new Float32Array(L * H)
  const cumul = new Float64Array(Math.max(L, H) + 1)
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < L; x++) cumul[x + 1] = cumul[x]! + v[y * L + x]!
    for (let x = 0; x < L; x++)
      tmp[y * L + x] = cumul[Math.min(L, x + r + 1)]! - cumul[Math.max(0, x - r)]!
  }
  for (let x = 0; x < L; x++) {
    for (let y = 0; y < H; y++) cumul[y + 1] = cumul[y]! + tmp[y * L + x]!
    for (let y = 0; y < H; y++)
      sommes[y * L + x] = cumul[Math.min(H, y + r + 1)]! - cumul[Math.max(0, y - r)]!
  }
  const cote = (c: number, max: number) => Math.min(max, c + r + 1) - Math.max(0, c - r)
  return { sommes, taille: (i) => cote(i % L, L) * cote(Math.floor(i / L), H) }
}

/** Flou en boîte de rayon `r`. */
function flouter(v: Float32Array, L: number, H: number, r: number): Float32Array {
  const { sommes, taille } = sommesBoite(v, L, H, r)
  for (let i = 0; i < sommes.length; i++) sommes[i] = sommes[i]! / taille(i)
  return sommes
}

/** Fermeture (dilatation puis érosion, carré de rayon `r`) : bouche les traits sombres fins. */
function fermer(m: Uint8Array, L: number, H: number, r: number): Uint8Array {
  const dilate = new Uint8Array(m.length)
  const d = sommesBoite(m, L, H, r).sommes
  for (let i = 0; i < m.length; i++) dilate[i] = d[i]! > 0.5 ? 1 : 0
  const ferme = new Uint8Array(m.length)
  const e = sommesBoite(dilate, L, H, r)
  for (let i = 0; i < m.length; i++) ferme[i] = e.sommes[i]! > e.taille(i) - 0.5 ? 1 : 0
  return ferme
}

/** Seuil d'Otsu (0 à 255), part de variance qu'il explique, écart des moyennes des deux classes. */
function otsu(v: Float32Array): { seuil: number; separation: number; ecart: number } | null {
  const histo = new Float64Array(256)
  for (let i = 0; i < v.length; i++) histo[Math.min(255, Math.max(0, Math.floor(v[i]!)))]!++
  const n = v.length
  let somme = 0
  for (let t = 0; t < 256; t++) somme += t * histo[t]!
  const moyenne = somme / n
  let variance = 0
  for (let t = 0; t < 256; t++) variance += histo[t]! * (t - moyenne) ** 2
  variance /= n
  if (variance <= 0) return null
  let meilleur = { seuil: 0, inter: -1, ecart: 0 }
  let poids0 = 0
  let somme0 = 0
  for (let t = 0; t < 255; t++) {
    poids0 += histo[t]!
    somme0 += t * histo[t]!
    const poids1 = n - poids0
    if (poids0 === 0 || poids1 === 0) continue
    const m0 = somme0 / poids0
    const m1 = (somme - somme0) / poids1
    const inter = (poids0 / n) * (poids1 / n) * (m0 - m1) ** 2
    if (inter > meilleur.inter) meilleur = { seuil: t, inter, ecart: m1 - m0 }
  }
  return { seuil: meilleur.seuil, separation: meilleur.inter / variance, ecart: meilleur.ecart }
}

/** Régions du masque (4-connexité) : étiquette de chaque pixel (0 : hors masque), aire de chacune. */
function regions(
  masque: Uint8Array,
  L: number,
  H: number,
): { etiquettes: Int32Array; aires: number[] } {
  const n = L * H
  const etiquettes = new Int32Array(n)
  const pile = new Int32Array(n)
  const aires = [0]
  for (let depart = 0; depart < n; depart++) {
    if (etiquettes[depart] !== 0 || !masque[depart]) continue
    const etiquette = aires.length
    let haut = 0
    let compte = 0
    const pousser = (j: number) => {
      if (masque[j] && etiquettes[j] === 0) {
        etiquettes[j] = etiquette
        pile[haut++] = j
      }
    }
    pousser(depart)
    while (haut > 0) {
      const i = pile[--haut]!
      compte++
      const x = i % L
      if (x > 0) pousser(i - 1)
      if (x < L - 1) pousser(i + 1)
      if (i >= L) pousser(i - L)
      if (i < n - L) pousser(i + L)
    }
    aires.push(compte)
  }
  return { etiquettes, aires }
}

/** Bords gauche et droit de la région sur chaque ligne (coins des pixels) : même enveloppe. */
function extremites(masque: Uint8Array, L: number, H: number): Point[] {
  const points: Point[] = []
  for (let y = 0; y < H; y++) {
    let gauche = -1
    let droite = -1
    for (let x = 0; x < L; x++)
      if (masque[y * L + x]) {
        if (gauche < 0) gauche = x
        droite = x
      }
    if (gauche < 0) continue
    points.push([gauche, y], [gauche, y + 1], [droite + 1, y], [droite + 1, y + 1])
  }
  return points
}

const produit = (o: Point, a: Point, b: Point) =>
  (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])

/** Enveloppe convexe (chaîne monotone d'Andrew), sommets dans l'ordre. */
function enveloppeConvexe(points: Point[]): Point[] {
  const p = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1])
  if (p.length < 3) return p
  const bas: Point[] = []
  for (const q of p) {
    while (bas.length >= 2 && produit(bas[bas.length - 2]!, bas[bas.length - 1]!, q) <= 0) bas.pop()
    bas.push(q)
  }
  const haut: Point[] = []
  for (const q of p.reverse()) {
    while (haut.length >= 2 && produit(haut[haut.length - 2]!, haut[haut.length - 1]!, q) <= 0)
      haut.pop()
    haut.push(q)
  }
  return [...bas.slice(0, -1), ...haut.slice(0, -1)]
}

/**
 * Quatre sommets de l'enveloppe qui enferment la plus grande aire : les coins de la feuille,
 * même arrondis par le flou. Une enveloppe très détaillée est d'abord allégée de ses sommets
 * les moins saillants (ceux qui portent le moins d'aire).
 */
function quadrilatereMax(enveloppe: Point[]): Point[] {
  const q = [...enveloppe]
  while (q.length > SOMMETS_MAX) {
    let k = 0
    let min = Number.POSITIVE_INFINITY
    for (let i = 0; i < q.length; i++) {
      const a = Math.abs(produit(q[(i + q.length - 1) % q.length]!, q[i]!, q[(i + 1) % q.length]!))
      if (a < min) {
        min = a
        k = i
      }
    }
    q.splice(k, 1)
  }
  let meilleur = q.slice(0, 4)
  let aireMax = -1
  const m = q.length
  for (let a = 0; a < m; a++)
    for (let b = a + 1; b < m; b++)
      for (let c = b + 1; c < m; c++)
        for (let d = c + 1; d < m; d++) {
          const s = aire([q[a]!, q[b]!, q[c]!, q[d]!])
          if (s > aireMax) {
            aireMax = s
            meilleur = [q[a]!, q[b]!, q[c]!, q[d]!]
          }
        }
  return meilleur
}

/**
 * Chaque côté du quadrilatère, gardé dans sa direction, est repoussé vers l'extérieur jusqu'à
 * laisser toute l'enveloppe dedans ; les nouveaux coins sont les intersections des côtés.
 */
function repousserCotes(quad: Point[], enveloppe: Point[]): Point[] | null {
  const cx = quad.reduce((s, p) => s + p[0], 0) / 4
  const cy = quad.reduce((s, p) => s + p[1], 0) / 4
  const cotes: { point: Point; dir: Point }[] = []
  for (let k = 0; k < 4; k++) {
    const a = quad[k]!
    const b = quad[(k + 1) % 4]!
    const lg = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (lg === 0) return null
    const dir: Point = [(b[0] - a[0]) / lg, (b[1] - a[1]) / lg]
    let nx = dir[1]
    let ny = -dir[0]
    if (nx * ((a[0] + b[0]) / 2 - cx) + ny * ((a[1] + b[1]) / 2 - cy) < 0) {
      nx = -nx
      ny = -ny
    }
    let decalage = 0
    for (const p of enveloppe)
      decalage = Math.max(decalage, (p[0] - a[0]) * nx + (p[1] - a[1]) * ny)
    cotes.push({ point: [a[0] + nx * decalage, a[1] + ny * decalage], dir })
  }
  const coins: Point[] = []
  for (let k = 0; k < 4; k++) {
    const c1 = cotes[(k + 3) % 4]!
    const c2 = cotes[k]!
    const det = c1.dir[0] * c2.dir[1] - c1.dir[1] * c2.dir[0]
    if (Math.abs(det) < 1e-6) return null
    const dx = c2.point[0] - c1.point[0]
    const dy = c2.point[1] - c1.point[1]
    const t = (dx * c2.dir[1] - dy * c2.dir[0]) / det
    coins.push([c1.point[0] + t * c1.dir[0], c1.point[1] + t * c1.dir[1]])
  }
  return coins
}

function aire(q: Point[]): number {
  let s = 0
  for (let k = 0; k < q.length; k++) {
    const a = q[k]!
    const b = q[(k + 1) % q.length]!
    s += a[0] * b[1] - b[0] * a[1]
  }
  return Math.abs(s) / 2
}

/** Angle en degrés au sommet `b`. */
function angle(a: Point, b: Point, c: Point): number {
  const u = [a[0] - b[0], a[1] - b[1]] as const
  const v = [c[0] - b[0], c[1] - b[1]] as const
  const cos = (u[0] * v[0] + u[1] * v[1]) / (Math.hypot(...u) * Math.hypot(...v))
  return (Math.acos(Math.min(1, Math.max(-1, cos))) * 180) / Math.PI
}

/** Aire de la région trous compris (le texte d'une feuille y fait des trous). */
function aireRemplie(masque: Uint8Array, L: number, H: number): number {
  // Le dehors : ce qui n'est pas la région et se rejoint depuis le bord de la photo.
  const n = L * H
  const vu = new Uint8Array(n)
  const pile = new Int32Array(n)
  let haut = 0
  const pousser = (i: number) => {
    if (!masque[i] && !vu[i]) {
      vu[i] = 1
      pile[haut++] = i
    }
  }
  for (let x = 0; x < L; x++) {
    pousser(x)
    pousser((H - 1) * L + x)
  }
  for (let y = 0; y < H; y++) {
    pousser(y * L)
    pousser(y * L + L - 1)
  }
  let dehors = 0
  while (haut > 0) {
    const i = pile[--haut]!
    dehors++
    const x = i % L
    if (x > 0) pousser(i - 1)
    if (x < L - 1) pousser(i + 1)
    if (i >= L) pousser(i - L)
    if (i < n - L) pousser(i + L)
  }
  return n - dehors
}
